import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Space,
  Alert,
  Spin,
  message,
  Avatar,
  Divider
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  SafetyOutlined,
  MailOutlined,
  LockOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import API_BASE_URL from '../helpers/api/apiConfig';

const { Title, Text, Paragraph } = Typography;

interface InvitationDetails {
  email: string;
  company: string;
  role: string;
  isMaster?: boolean;
  invitedBy: string;
  message?: string;
  validUntil: string;
  userExisted: boolean;
}

const AcceptInvitation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitationDetails, setInvitationDetails] = useState<InvitationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (token) {
      fetchInvitationDetails();
    }
  }, [token]);

  const fetchInvitationDetails = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/user-invitations/details/${token}`);
      
      if (response.ok) {
        const data = await response.json();
        setInvitationDetails(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Invalid or expired invitation');
      }
    } catch (error) {
      console.error('Error fetching invitation details:', error);
      setError('Failed to load invitation details');
    }
    setLoading(false);
  };

  const handleAcceptInvitation = async (values: any) => {
    setSubmitting(true);
    
    try {
      const payload = invitationDetails?.userExisted 
        ? {} // Existing users don't need to provide password
        : { password: values.password };

      const response = await fetch(`${API_BASE_URL}/user-invitations/accept/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        setAccepted(true);
        message.success('Invitation accepted successfully!');
        
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: `Welcome to ${invitationDetails?.company}! Please log in with your credentials.`,
              email: invitationDetails?.email
            } 
          });
        }, 3000);
      } else {
        const errorData = await response.json();
        message.error(errorData.message || 'Failed to accept invitation');
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      message.error('Failed to accept invitation');
    }
    
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f0f2f5'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f0f2f5',
        padding: '20px'
      }}>
        <Card style={{ maxWidth: '500px', width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Avatar size={64} icon={<UserOutlined />} style={{ backgroundColor: '#ff4d4f' }} />
            <Title level={3} style={{ marginTop: '16px', color: '#ff4d4f' }}>
              Invalid Invitation
            </Title>
            <Paragraph>
              {error}
            </Paragraph>
            <Button type="primary" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (accepted) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f0f2f5',
        padding: '20px'
      }}>
        <Card style={{ maxWidth: '500px', width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a' }} />
            <Title level={3} style={{ marginTop: '16px', color: '#52c41a' }}>
              Welcome to {invitationDetails?.company}!
            </Title>
            <Paragraph>
              Your invitation has been accepted successfully. You will be redirected to the login page shortly.
            </Paragraph>
            <Alert
              message="Next Steps"
              description={`You can now log in with your email (${invitationDetails?.email}) ${invitationDetails?.userExisted ? 'and existing password' : 'and the password you just created'}.`}
              type="success"
              showIcon
              style={{ textAlign: 'left' }}
            />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f0f2f5',
      padding: '20px'
    }}>
      <Card style={{ maxWidth: '600px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Avatar size={64} icon={<TeamOutlined />} style={{ backgroundColor: '#1890ff' }} />
          <Title level={2} style={{ marginTop: '16px' }}>
            You're Invited!
          </Title>
        </div>

        <Card 
          type="inner" 
          title={
            <Space>
              <MailOutlined />
              Invitation Details
            </Space>
          }
          style={{ marginBottom: '24px' }}
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div>
              <Text strong>Email: </Text>
              <Text>{invitationDetails?.email}</Text>
            </div>
            <div>
              <Text strong>Company: </Text>
              <Text>{invitationDetails?.company}</Text>
            </div>
            <div>
              <Text strong>Role: </Text>
              <Space>
                <SafetyOutlined />
                <Text>{invitationDetails?.role}</Text>
                {invitationDetails?.isMaster && <Text type="warning">(Master)</Text>}
              </Space>
            </div>
            <div>
              <Text strong>Invited by: </Text>
              <Text>{invitationDetails?.invitedBy}</Text>
            </div>
            <div>
              <Text strong>Valid until: </Text>
              <Text>{new Date(invitationDetails?.validUntil || '').toLocaleDateString()}</Text>
            </div>
          </Space>

          {invitationDetails?.message && (
            <>
              <Divider />
              <div>
                <Text strong>Personal Message:</Text>
                <Paragraph style={{ marginTop: '8px', fontStyle: 'italic' }}>
                  "{invitationDetails.message}"
                </Paragraph>
              </div>
            </>
          )}
        </Card>

        <Alert
          message={invitationDetails?.userExisted 
            ? "Account Found" 
            : "New Account Setup"}
          description={invitationDetails?.userExisted
            ? "We found an existing account with your email. Accepting this invitation will add you to the company."
            : "We'll create a new account for you. Please set a password to complete your registration."
          }
          type={invitationDetails?.userExisted ? "info" : "success"}
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleAcceptInvitation}
        >
          {!invitationDetails?.userExisted && (
            <Form.Item
              name="password"
              label="Create Password"
              rules={[
                { required: true, message: 'Password is required' },
                { min: 6, message: 'Password must be at least 6 characters' }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Create a secure password"
                size="large"
              />
            </Form.Item>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              size="large"
              block
              icon={<CheckCircleOutlined />}
            >
              {invitationDetails?.userExisted 
                ? 'Accept Invitation' 
                : 'Accept Invitation & Create Account'}
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Button type="link" onClick={() => navigate('/login')}>
            Already have an account? Sign in
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AcceptInvitation;
